<template>
  <b-container fluid class="idd">
    <!-- Chatbot intro -->
    <div class="idd-chat">
      <div class="idd-bubble">
        <p>
          <strong
            >¡Hola! Así de fácil es llenar la documentación PIE en Utopie.</strong
          >
        </p>
        <span>
          Elige una sugerencia en el <b>Informe a la familia</b> y observa cómo
          la información se transfiere sola a la <b>FU de reevaluación</b>.
        </span>
      </div>
    </div>

    <template v-if="!completed">
      <!-- Progress -->
      <div class="idd-progress">
        <div class="idd-progress-ring" :style="{ '--p': progressPct + '%' }">
          <span>{{ filledCount }}/{{ steps.length }}</span>
        </div>
        <div class="idd-progress-copy">
          <strong>Completa {{ steps.length }} campos</strong>
          <small>{{ progressHint }}</small>
        </div>
      </div>

      <!-- Transfer workspace -->
      <div class="idd-workspace">
        <!-- Source: one suggestion section at a time -->
        <section class="idd-panel idd-source">
          <header class="idd-panel-head">
            <span class="idd-doc-name">Informe a la familia</span>
            <span class="idd-chip-tag">Origen</span>
          </header>
          <div class="idd-section">
            <div class="idd-section-title">
              <span class="idd-step-dot">{{ currentIndex + 1 }}</span>
              <h4>{{ currentStep.sourceTitle }}</h4>
            </div>
            <div class="idd-options">
              <button
                v-for="(opt, oi) in currentStep.options"
                :key="oi"
                type="button"
                class="idd-option"
                :disabled="animating"
                @click="selectOption(opt, $event)"
              >
                <span class="idd-radio"></span>
                <span class="idd-option-text">{{ opt }}</span>
              </button>
            </div>
          </div>
        </section>

        <!-- Flow indicator -->
        <div class="idd-flow" :class="{ 'idd-flow-pulse': animating }">
          <svg viewBox="0 0 24 24">
            <path d="M4 12h15" />
            <path d="M13 6l6 6-6 6" />
          </svg>
        </div>

        <!-- Target: the FU fields filling up -->
        <section class="idd-panel idd-target">
          <header class="idd-panel-head idd-panel-head-target">
            <span class="idd-doc-name">FU de reevaluación</span>
            <span class="idd-chip-tag">Destino</span>
          </header>
          <div class="idd-fields">
            <div
              v-for="(step, si) in steps"
              :key="step.key"
              :ref="'field-' + step.key"
              class="idd-field"
              :class="{
                'idd-field-active': si === currentIndex && !filled[step.key],
                'idd-field-filled': !!filled[step.key],
              }"
            >
              <span class="idd-field-label">{{ step.targetLabel }}</span>
              <div class="idd-field-content">
                <span v-if="filled[step.key]" class="idd-field-text">{{
                  filled[step.key]
                }}</span>
                <span v-else class="idd-field-placeholder"
                  >Se completará al escoger una sugerencia.</span
                >
              </div>
            </div>
          </div>
        </section>
      </div>
    </template>

    <!-- Completion + login CTA -->
    <div v-else class="idd-done">
      <div class="idd-done-icon">✓</div>
      <p class="idd-done-title">¡Así de fácil es llenar un documento en Utopie!</p>
      <p class="idd-done-text" v-if="bonusHtml" v-html="bonusHtml"></p>
      <p class="idd-done-text" v-else>
        ¡Inicia sesión y podrás llenar tus propios documentos gratis!
      </p>
      <button type="button" class="idd-login-btn" @click="login">
        Iniciar sesión
      </button>
      <button type="button" class="idd-retry-btn" @click="restart">
        Probar de nuevo
      </button>
      <button type="button" class="idd-retry-btn" @click="backToMenu">
        Volver al menú
      </button>
    </div>

    <!-- The document that "flies" from a suggestion to its FU field -->
    <div v-show="flying" ref="chip" class="idd-fly-chip">
      <div class="idd-fly-bar"></div>
      <div class="idd-fly-body">
        <span class="idd-fly-ico">
          <svg viewBox="0 0 24 24">
            <path d="M14 3v5h5" />
            <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
          </svg>
        </span>
        <span class="idd-fly-text">{{ chipText }}</span>
      </div>
    </div>
  </b-container>
</template>

<script>
import { fetchRewards, bonusLine } from "./signupRewards";
import {
  medirDemoIniciada,
  medirDemoTerminada,
} from "@/services/analytics";
// Logged-out "try it free" demo for the Individual Documents chatbot. It
// reproduces the signature interaction of the product — picking a suggestion in
// one document auto-fills the matching field of a second document — using a
// small, static TDA sample ("Informe a la familia" -> "FU de reevaluación").
// Nothing is persisted or downloaded; once the sample sheet is complete it
// invites the visitor to log in (`go-to-step` "login", resuelto por el chat).
const STEPS = [
  {
    key: "DATOSDELPROFESIONALQUEEMITENUEVODIAGNOSTICO",
    sourceTitle: "¿Quién emite el nuevo diagnóstico?",
    targetLabel: "¿Quién emite nuevo diagnóstico?",
    options: [
      "Psicólogo/a",
      "Educadora Diferencial",
      "Fonoaudiólogo/a",
      "Neurólogo",
    ],
  },
  {
    key: "REGISTRENUEVASEVALUACIONES",
    sourceTitle: "Nueva evaluación aplicada",
    targetLabel:
      "Registre nuevas evaluaciones o revaluaciones realizadas y las modificaciones al diagnóstico si las hubiere.",
    options: ["Psicológica", "Psicopedagógica", "Fonoaudiológica", "Médica"],
  },
  {
    key: "PROGRESOSDELESTUDIANTE",
    sourceTitle: "Principales progresos del estudiante",
    targetLabel:
      "Describa los principales progresos del estudiante en su proceso educativo y en la evolución de sus NEE.",
    options: [
      "Ha mostrado avances cuando recibe apoyo individual y una guía constante, logrando completar actividades de manera más efectiva.",
      "Ha mejorado en el área cognitiva, mostrando una mayor concentración en clases y avances en áreas instrumentales.",
      "Ha demostrado un mayor compromiso con el proceso de aprendizaje, mejorando la interacción con sus compañeros y la comunicación con los docentes.",
    ],
  },
  {
    key: "AREASDEDIFICULTADENAPRENDIZAJE",
    sourceTitle: "Áreas de dificultad en el aprendizaje",
    targetLabel:
      "Resuma las principales áreas en las que el/la estudiante presenta dificultades en su aprendizaje.",
    options: [
      "Requiere apoyo en las áreas de lenguaje y matemáticas, especialmente en la escritura espontánea, comprensión lectora y resolución de problemas.",
      "Aún presenta dificultades en la lectura y en las operaciones básicas de matemáticas, lo que requiere refuerzo constante.",
      "Se observan dificultades en la comprensión lectora, especialmente en la extracción de información implícita, y en el desempeño matemático.",
    ],
  },
];

export default {
  // Hoja de ejemplo del chatbot de Documentos PIE. Vive dentro de
  // IndividualDocumentsDemo como paso `custom-component`, así que no navega ni
  // abre el login por su cuenta: pide los dos con `go-to-step` y el chat los
  // resuelve (es el que cuelga del router-view y llega al layout).
  name: "DocumentsSheetDemo",
  data() {
    return {
      steps: STEPS,
      currentIndex: 0,
      filled: {},
      animating: false,
      flying: false,
      chipText: "",
      // Usos gratis + misiones, con los números reales de la config.
      rewards: null,
      // La hoja se cuenta una vez por montaje, y su final es que el
      // visitante la haya llenado entera.
      inicioContado: false,
      finContado: false,
    };
  },
  created() {
    // El componente se monta cuando el chat llega al paso de la hoja:
    // montarse ES empezar la demostracion.
    this.contarInicio();
    fetchRewards("Documentos PIE").then((rewards) => {
      this.rewards = rewards;
    });
  },
  watch: {
    // Celebrate when the sample sheet is complete (the login CTA view).
    completed(done) {
      if (!done) return;
      this.contarFin();
      this.$nextTick(() => this.spawnConfetti());
    },
  },
  beforeDestroy() {
    // Remove any confetti still falling if the user navigates away mid-burst.
    (this._confetti || []).forEach((el) => el.remove());
    this._confetti = [];
  },
  computed: {
    bonusHtml() {
      return bonusLine(this.rewards);
    },
    currentStep() {
      return this.steps[this.currentIndex] || this.steps[0];
    },
    filledCount() {
      return this.steps.filter((s) => this.filled[s.key]).length;
    },
    completed() {
      return this.filledCount >= this.steps.length;
    },
    progressPct() {
      return this.steps.length
        ? (this.filledCount / this.steps.length) * 100
        : 0;
    },
    progressHint() {
      const left = this.steps.length - this.filledCount;
      return left > 0
        ? `Faltan ${left} sugerencia(s) para completar la hoja.`
        : "¡Hoja lista!";
    },
  },
  methods: {
    excerpt(text) {
      return text.length > 110 ? text.slice(0, 110) + "…" : text;
    },
    selectOption(option, event) {
      if (this.animating || this.filled[this.currentStep.key]) return;
      this.animating = true;
      const sourceEl = event.currentTarget;
      const refs = this.$refs["field-" + this.currentStep.key];
      const targetEl = Array.isArray(refs) ? refs[0] : refs;
      this.flyChip(sourceEl, targetEl, option).then(() => {
        this.$set(this.filled, this.currentStep.key, option);
        this.animating = false;
        // Advance to the next not-yet-filled field.
        const next = this.steps.findIndex((s) => !this.filled[s.key]);
        if (next !== -1) this.currentIndex = next;
      });
    },
    // Animate a small "document" from the chosen suggestion to its FU field.
    flyChip(sourceEl, targetEl, text) {
      return new Promise((resolve) => {
        const chip = this.$refs.chip;
        if (!sourceEl || !targetEl || !chip) {
          resolve();
          return;
        }
        const s = sourceEl.getBoundingClientRect();
        const t = targetEl.getBoundingClientRect();
        this.chipText = this.excerpt(text);
        chip.style.left = s.left + "px";
        chip.style.top = s.top + "px";
        chip.style.width = Math.min(260, Math.max(180, s.width * 0.7)) + "px";
        this.flying = true;
        this.$nextTick(() => {
          const dx = t.left + 16 - s.left;
          const dy = t.top + 16 - s.top;
          const arc = Math.min(60, Math.abs(dx) * 0.12 + 22);
          const anim = chip.animate(
            [
              {
                transform: "translate3d(0,0,0) rotate(-1.5deg) scale(.96)",
                opacity: 0,
              },
              {
                transform: `translate3d(${dx * 0.3}px,${
                  dy * 0.3 - arc
                }px,0) rotate(3deg) scale(1.03)`,
                opacity: 1,
                offset: 0.34,
              },
              {
                transform: `translate3d(${dx * 0.72}px,${
                  dy * 0.72 - arc * 0.5
                }px,0) rotate(1.5deg) scale(1)`,
                opacity: 1,
                offset: 0.72,
              },
              {
                transform: `translate3d(${dx}px,${dy}px,0) rotate(-1deg) scale(.9)`,
                opacity: 0,
              },
            ],
            { duration: 820, easing: "cubic-bezier(.33,0,.2,1)" }
          );
          anim.onfinish = () => {
            this.flying = false;
            resolve();
          };
        });
      });
    },
    // Falling confetti celebration (WAAPI so it needs no global CSS). Pieces are
    // appended to <body>, animated to the bottom of the viewport and removed.
    contarInicio() {
      if (this.inicioContado) return;
      this.inicioContado = true;
      medirDemoIniciada("Documentos PIE");
    },
    contarFin() {
      if (this.finContado) return;
      this.finContado = true;
      medirDemoTerminada("Documentos PIE");
    },
    spawnConfetti() {
      const colors = [
        "#0f4f75",
        "#176899",
        "#208ac6",
        "#44a7db",
        "#7dd0f3",
        "#ffffff",
        "#f8ac04",
      ];
      this._confetti = this._confetti || [];
      const fall = window.innerHeight + 40;
      for (let i = 0; i < 90; i += 1) {
        const piece = document.createElement("span");
        const color = colors[Math.floor(Math.random() * colors.length)];
        piece.style.cssText =
          "position:fixed;top:-16px;left:" +
          Math.random() * 100 +
          "vw;width:9px;height:13px;border-radius:2px;z-index:1090;" +
          "pointer-events:none;background:" +
          color +
          ";opacity:" +
          (0.72 + Math.random() * 0.28) +
          ";";
        document.body.appendChild(piece);
        this._confetti.push(piece);
        const drift = (Math.random() * 2 - 1) * 90;
        const spin = 480 + Math.random() * 540;
        const anim = piece.animate(
          [
            { transform: "translate(0,0) rotate(0deg)", opacity: 1 },
            {
              transform:
                "translate(" + drift + "px," + fall + "px) rotate(" + spin + "deg)",
              opacity: 0.65,
            },
          ],
          {
            duration: 1700 + Math.random() * 1900,
            delay: Math.random() * 280,
            easing: "cubic-bezier(.2,.6,.5,1)",
            fill: "forwards",
          }
        );
        anim.onfinish = () => {
          piece.remove();
          const idx = this._confetti.indexOf(piece);
          if (idx !== -1) this._confetti.splice(idx, 1);
        };
      }
    },
    restart() {
      this.filled = {};
      this.currentIndex = 0;
      this.animating = false;
      this.flying = false;
    },
    login() {
      this.$emit("go-to-step", "login");
    },
    backToMenu() {
      this.$emit("go-to-step", "menu");
    },
  },
};
</script>

<style scoped>
.idd {
  --product: #208ac6;
  --product-dark: #176899;
  --product-deep: #0f4f75;
  --muted: #6d7788;
  --navy: #1a2332;
  max-width: 1100px;
  margin: 0 auto;
  padding: 18px 10px 40px;
  font-family: Ubuntu, "Segoe UI", system-ui, sans-serif;
  color: var(--navy);
}

/* Chat intro */
.idd-chat {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 18px;
}
.idd-bubble {
  width: min(760px, 92%);
  border: 2px dashed var(--product);
  border-radius: 22px;
  background: #fff;
  padding: 18px 22px;
  text-align: center;
  box-shadow: 0 16px 36px rgba(32, 138, 198, 0.11);
  color: var(--product-deep);
}
.idd-bubble p {
  margin: 0 0 6px;
  font-size: 1.06rem;
  line-height: 1.35;
}
.idd-bubble span {
  display: block;
  color: var(--muted);
  font-weight: 600;
  line-height: 1.4;
}

/* Progress */
.idd-progress {
  display: flex;
  align-items: center;
  gap: 14px;
  border: 1px solid rgba(32, 138, 198, 0.16);
  border-radius: 18px;
  background: #fff;
  box-shadow: 0 12px 30px rgba(26, 35, 50, 0.06);
  padding: 12px 16px;
  margin-bottom: 16px;
}
.idd-progress-ring {
  --p: 0%;
  width: 52px;
  height: 52px;
  border-radius: 999px;
  background: conic-gradient(var(--product) var(--p), #edf1f5 0);
  display: grid;
  place-items: center;
  flex: 0 0 auto;
  transition: background 0.38s ease;
}
.idd-progress-ring span {
  width: 40px;
  height: 40px;
  border-radius: 999px;
  background: #fff;
  display: grid;
  place-items: center;
  color: var(--product-deep);
  font-weight: 800;
  font-size: 0.8rem;
}
.idd-progress-copy strong {
  display: block;
  color: var(--navy);
  font-weight: 800;
}
.idd-progress-copy small {
  display: block;
  color: var(--muted);
  font-weight: 600;
  margin-top: 2px;
}

/* Workspace */
.idd-workspace {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 70px minmax(0, 1fr);
  gap: 12px;
  align-items: stretch;
}
.idd-panel {
  min-width: 0;
  border-radius: 20px;
  background: #fff;
  border: 1px solid rgba(15, 79, 117, 0.12);
  box-shadow: 0 18px 46px rgba(23, 104, 153, 0.1);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
.idd-panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 13px 15px;
  background: linear-gradient(90deg, #176899, #208ac6);
  color: #fff;
}
.idd-doc-name {
  font-size: 0.95rem;
  font-weight: 800;
}
.idd-chip-tag {
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.18);
  border: 1px solid rgba(255, 255, 255, 0.32);
  padding: 4px 10px;
  font-size: 0.62rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

/* Source section */
.idd-section {
  padding: 16px;
}
.idd-section-title {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
}
.idd-step-dot {
  width: 28px;
  height: 28px;
  border-radius: 9px;
  background: linear-gradient(135deg, #176899, #44a7db);
  color: #fff;
  display: grid;
  place-items: center;
  font-weight: 800;
  font-size: 0.8rem;
  flex: 0 0 auto;
}
.idd-section-title h4 {
  margin: 0;
  font-size: 0.98rem;
  line-height: 1.25;
  color: #10263d;
  font-weight: 800;
}
.idd-options {
  display: grid;
  gap: 8px;
}
.idd-option {
  display: grid;
  grid-template-columns: 20px minmax(0, 1fr);
  gap: 10px;
  align-items: flex-start;
  text-align: left;
  border: 1px solid rgba(26, 35, 50, 0.09);
  border-radius: 12px;
  background: #fff;
  padding: 11px 12px;
  cursor: pointer;
  transition: 0.16s ease;
}
.idd-option:hover:not(:disabled) {
  border-color: rgba(32, 138, 198, 0.45);
  box-shadow: 0 8px 18px -6px rgba(32, 138, 198, 0.3);
  transform: translateY(-1px);
}
.idd-option:disabled {
  cursor: default;
  opacity: 0.85;
}
.idd-radio {
  width: 16px;
  height: 16px;
  margin-top: 2px;
  border-radius: 999px;
  border: 1.6px solid rgba(32, 138, 198, 0.5);
  background: #fff;
  flex: 0 0 auto;
}
.idd-option-text {
  color: #26384b;
  font-size: 0.84rem;
  line-height: 1.34;
  font-weight: 600;
}

/* Flow indicator */
.idd-flow {
  display: grid;
  place-items: center;
}
.idd-flow svg {
  width: 26px;
  height: 26px;
  fill: none;
  stroke: var(--product);
  stroke-width: 2.4;
  stroke-linecap: round;
  stroke-linejoin: round;
  filter: drop-shadow(0 4px 10px rgba(32, 138, 198, 0.25));
}
.idd-flow-pulse svg {
  animation: iddNudge 0.6s ease infinite;
}
@keyframes iddNudge {
  0%,
  100% {
    transform: translateX(0);
  }
  50% {
    transform: translateX(5px);
  }
}

/* Target fields */
.idd-fields {
  padding: 16px;
  display: grid;
  gap: 10px;
}
.idd-field {
  border: 1px solid rgba(15, 79, 117, 0.14);
  border-radius: 14px;
  background: #fff;
  padding: 13px 14px;
  transition: 0.24s ease;
}
.idd-field-active {
  border-color: rgba(32, 138, 198, 0.4);
  background: #f3faff;
  box-shadow: 0 12px 26px -12px rgba(32, 138, 198, 0.4);
}
.idd-field-filled {
  border-color: rgba(57, 165, 106, 0.3);
  background: linear-gradient(180deg, #fff, #fafffc);
}
.idd-field-label {
  display: block;
  margin-bottom: 8px;
  color: #10263d;
  font-weight: 800;
  font-size: 0.8rem;
  line-height: 1.3;
}
.idd-field-content {
  border: 1px dashed #cdd7e2;
  border-radius: 11px;
  background: #f8fafc;
  min-height: 56px;
  padding: 10px 12px;
  color: #7a8696;
  font-weight: 600;
  line-height: 1.42;
  font-size: 0.83rem;
}
.idd-field-filled .idd-field-content {
  border-style: solid;
  border-color: rgba(32, 138, 198, 0.3);
  background: #fff;
  color: #243748;
}
.idd-field-text {
  display: block;
  animation: iddType 0.42s ease both;
}
@keyframes iddType {
  from {
    opacity: 0;
    transform: translateY(7px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Flying document */
.idd-fly-chip {
  position: fixed;
  z-index: 1080;
  pointer-events: none;
  border-radius: 14px;
  background: linear-gradient(160deg, #fff, #f4faff);
  border: 1px solid rgba(32, 138, 198, 0.3);
  box-shadow: 0 26px 60px rgba(15, 79, 117, 0.28);
  overflow: hidden;
  will-change: transform, opacity;
}
.idd-fly-bar {
  height: 6px;
  background: linear-gradient(90deg, #0f4f75, #208ac6, #7dd0f3);
}
.idd-fly-body {
  display: flex;
  gap: 9px;
  align-items: flex-start;
  padding: 10px 12px;
}
.idd-fly-ico {
  flex: 0 0 auto;
  width: 26px;
  height: 26px;
  border-radius: 8px;
  background: linear-gradient(135deg, #208ac6, #7dd0f3);
  display: grid;
  place-items: center;
}
.idd-fly-ico svg {
  width: 15px;
  height: 15px;
  fill: none;
  stroke: #fff;
  stroke-width: 2.2;
  stroke-linecap: round;
  stroke-linejoin: round;
}
.idd-fly-text {
  min-width: 0;
  font-weight: 600;
  font-size: 0.76rem;
  line-height: 1.32;
  color: #28435c;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Completion + login CTA */
.idd-done {
  max-width: 620px;
  margin: 8px auto 0;
  text-align: center;
  border-radius: 26px;
  border: 1px solid rgba(32, 138, 198, 0.2);
  background: radial-gradient(
      circle at 50% 0%,
      rgba(125, 208, 243, 0.2),
      transparent 36%
    ),
    linear-gradient(180deg, #fff, #f5fbff);
  box-shadow: 0 22px 60px rgba(23, 104, 153, 0.12);
  padding: 38px 26px 30px;
}
.idd-done-icon {
  width: 74px;
  height: 74px;
  border-radius: 24px;
  margin: 0 auto 16px;
  display: grid;
  place-items: center;
  color: #fff;
  font-size: 2rem;
  font-weight: 900;
  background: linear-gradient(135deg, #176899, #208ac6 58%, #7dd0f3);
  box-shadow: 0 18px 38px rgba(32, 138, 198, 0.28);
  animation: iddPop 0.45s cubic-bezier(0.2, 1.5, 0.4, 1) both;
}
@keyframes iddPop {
  from {
    opacity: 0;
    transform: scale(0.4);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}
.idd-done-title {
  margin: 0 0 8px;
  font-size: clamp(1.35rem, 2.4vw, 1.8rem);
  font-weight: 900;
  letter-spacing: -0.02em;
  color: var(--navy);
}
.idd-done-text {
  margin: 0 auto 20px;
  max-width: 460px;
  color: var(--muted);
  font-weight: 600;
  line-height: 1.45;
}
.idd-login-btn {
  min-width: min(100%, 320px);
  min-height: 52px;
  border: 0;
  border-radius: 999px;
  padding: 0 28px;
  background: linear-gradient(90deg, #0f4f75, #208ac6);
  color: #fff;
  font-weight: 900;
  font-size: 1rem;
  box-shadow: 0 16px 34px rgba(32, 138, 198, 0.26);
  transition: transform 0.18s ease, box-shadow 0.18s ease;
  animation: iddBlink 1.4s ease-in-out infinite;
}
.idd-login-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 22px 42px rgba(32, 138, 198, 0.32);
}
@keyframes iddBlink {
  0%,
  100% {
    transform: translateY(0);
    box-shadow: 0 16px 34px rgba(32, 138, 198, 0.26);
  }
  50% {
    transform: translateY(-3px);
    box-shadow: 0 22px 42px rgba(32, 138, 198, 0.36);
  }
}
.idd-retry-btn {
  display: block;
  margin: 14px auto 0;
  border: 0;
  background: transparent;
  color: var(--product-dark);
  font-weight: 700;
  font-size: 0.86rem;
  cursor: pointer;
  text-decoration: underline;
}

@media (max-width: 820px) {
  .idd-workspace {
    grid-template-columns: 1fr;
    gap: 10px;
  }
  .idd-flow {
    height: 44px;
  }
  .idd-flow svg {
    transform: rotate(90deg);
  }
}
</style>
